import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { PhoneInput } from '../ui/phone-input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Switch } from '../ui/switch'
import { CountrySelect } from '../ui/country-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Client, ClientCreate, ClientUpdate } from '../../types/client'

interface ClientFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ClientCreate | ClientUpdate) => Promise<void>
  client?: Client | null
  isLoading?: boolean
}

export function ClientForm({
  open,
  onClose,
  onSubmit,
  client,
  isLoading = false,
}: ClientFormProps) {
  const { t } = useTranslation()
  const isEditing = !!client

  const [formData, setFormData] = useState<ClientCreate>({
    name: '',
    taxId: '',
    street: '',
    city: '',
    postalCode: '',
    countryCode: '',
    email: '',
    phone: '',
    notes: '',
    isDefault: false,
  })

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        taxId: client.taxId || '',
        street: client.street || '',
        city: client.city || '',
        postalCode: client.postalCode || '',
        countryCode: client.countryCode || '',
        email: client.email || '',
        phone: client.phone || '',
        notes: client.notes || '',
        isDefault: client.isDefault,
      })
    } else {
      setFormData({
        name: '',
        taxId: '',
        street: '',
        city: '',
        postalCode: '',
        countryCode: '',
        email: '',
        phone: '',
        notes: '',
        isDefault: false,
      })
    }
  }, [client, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t('settings.clients.edit')
                : t('settings.clients.add')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.clients.formDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">{t('settings.clients.name')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('settings.clients.namePlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="taxId">{t('settings.clients.taxId')}</Label>
                <Input
                  id="taxId"
                  value={formData.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                  placeholder={t('settings.clients.taxIdPlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="street">{t('settings.clients.street')}</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                placeholder={t('settings.clients.streetPlaceholder')}
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="city">{t('settings.clients.city')}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder={t('settings.clients.cityPlaceholder')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="postalCode">{t('settings.clients.postalCode')}</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder={t('settings.clients.postalCodePlaceholder')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="country">{t('settings.clients.country')}</Label>
                <CountrySelect
                  id="country"
                  value={formData.countryCode}
                  onChange={(value) => setFormData({ ...formData, countryCode: value })}
                  disabled={isLoading}
                  placeholder={t('settings.clients.selectCountry')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('settings.clients.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('settings.clients.emailPlaceholder')}
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="phone">{t('settings.clients.phone')}</Label>
                <PhoneInput
                  id="phone"
                  value={formData.phone}
                  onChange={(val) => setFormData({ ...formData, phone: val })}
                  placeholder={t('settings.clients.phonePlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">{t('settings.clients.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('settings.clients.notesPlaceholder')}
                rows={2}
                disabled={isLoading}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isDefault: checked })
                }
                disabled={isLoading}
              />
              <Label htmlFor="isDefault">
                {t('settings.clients.setAsDefault')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
